"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { Activity, AppWindow, ArrowRight, ChevronDown, Cloud, ExternalLink, Heart, LayoutGrid, LogIn, LogOut, Mail, Menu, MonitorCloud, Shield, User, X } from "lucide-react";

type NavbarClientProps = {
  user: { email: string; name: string | null; role: string } | null;
};

const navigation = [
  { href: "/", title: "홈" },
  { href: "/pricing", title: "서비스" },
  { href: "/about", title: "소개" },
  { href: "/docs", title: "문서" },
  { href: "/contact", title: "문의" },
];
const panelLinks = [
  { title: "마크 서버 패널", description: "콘솔, 파일과 서버 관리", href: "https://panel.lighthost.it.kr", icon: AppWindow, ready: true, external: true },
  { title: "메일 대시보드", description: "도메인, DNS와 메일함 관리", href: "/my/mail", icon: Mail, ready: true, external: false },
  { title: "Cloud 패널", description: "스토리지와 파일 관리", href: "https://cloud.lighthost.it.kr", icon: Cloud, ready: true, external: true },
  { title: "VM 서버 패널", description: "가상서버 관리 콘솔", href: "", icon: MonitorCloud, ready: false, external: false },
];
const primaryButton = "inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#25242b] px-4 text-sm font-semibold text-[#ffffff] transition hover:bg-[#403b49] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#b66dff]";
const secondaryButton = "inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#e3e0e8] bg-white px-3 text-sm font-semibold text-[#45404d] transition hover:border-[#c9c0d5] hover:bg-[#f8f7fa] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#b66dff]";

export default function NavbarClient({ user }: NavbarClientProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelButtonRef = useRef<HTMLButtonElement>(null);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);
  const isAdmin = user?.role === "ADMIN";
  const displayName = user?.name ?? user?.email.split("@")[0];

  useEffect(() => {
    if (!panelOpen && !mobileOpen) return;
    function onPointerDown(event: PointerEvent) {
      if (event.target instanceof Node && !panelRef.current?.contains(event.target)) setPanelOpen(false);
    }
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (panelOpen) { setPanelOpen(false); panelButtonRef.current?.focus(); }
      if (mobileOpen) { setMobileOpen(false); mobileButtonRef.current?.focus(); }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [panelOpen, mobileOpen]);

  return (
    <header className="sticky top-0 z-50 border-b border-[#e9e7ed] bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4">
        <Link href="/" onClick={() => { setMobileOpen(false); setPanelOpen(false); }} className="flex shrink-0 items-center">
          <img src="/assets/logo.svg" alt="LightHost Logo" className="h-7 w-auto xl:h-8" />
        </Link>

        <nav aria-label="주 메뉴" className="hidden shrink-0 items-center gap-5 whitespace-nowrap text-sm text-[#6b6873] xl:flex">
          {navigation.map(item => <Link key={item.href} href={item.href} className="hover:text-[#8f4fe8]">{item.title}</Link>)}
          <a href="https://status.lighthost.it.kr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#8f4fe8]">
            <Activity size={15} aria-hidden="true" />서버 상태
          </a>
          <Link href="/donate" className="inline-flex items-center gap-1.5 text-[#8f4fe8] hover:text-[#7b3fd1]"><Heart size={15} aria-hidden="true" />후원</Link>
        </nav>

        <div className="hidden shrink-0 items-center gap-2 xl:flex">
          <div ref={panelRef} className="relative">
            <button ref={panelButtonRef} id="header-panel-trigger" type="button" aria-expanded={panelOpen} aria-controls="header-panels"
              onClick={() => setPanelOpen(current => !current)} className={secondaryButton}>
              <LayoutGrid size={16} aria-hidden="true" />패널 바로가기
              <ChevronDown size={14} aria-hidden="true" className={`shrink-0 transition ${panelOpen ? "rotate-180" : ""}`} />
            </button>
            {panelOpen && (
              <div id="header-panels" aria-labelledby="header-panel-trigger" className="absolute right-0 mt-2 w-80 rounded-lg border border-[#e3e0e8] bg-white p-2 shadow-lg shadow-[#25242b]/10">
                <p className="px-3 pb-2 pt-3 text-xs font-semibold text-[#96919d]">관리 콘솔</p>
                {panelLinks.map(item => {
                  const Icon = item.icon;
                  const content = <>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#f5f4f7] text-[#77717e]"><Icon size={18} aria-hidden="true" /></span>
                    <span className="min-w-0 flex-1"><span className="block text-sm font-semibold text-[#28242d]">{item.title}</span><span className="mt-0.5 block text-xs text-[#77717e]">{item.description}</span></span>
                  </>;
                  return item.ready ? (
                    <Link key={item.title} href={item.href} target={item.external ? "_blank" : undefined} rel={item.external ? "noreferrer" : undefined}
                      onClick={() => setPanelOpen(false)} className="flex items-center gap-3 rounded-md p-3 transition hover:bg-[#f8f7fa] focus-visible:outline-[#b66dff]">
                      {content}{item.external ? <ExternalLink size={14} aria-hidden="true" className="text-[#96919d]" /> : <ArrowRight size={14} aria-hidden="true" className="text-[#96919d]" />}
                    </Link>
                  ) : <div key={item.title} aria-disabled="true" className="flex items-center gap-3 p-3 opacity-60">{content}<span className="text-[11px] text-[#77717e]">준비 중</span></div>;
                })}
              </div>
            )}
          </div>
          {user ? (
            <Link href="/my" title="마이페이지" onClick={() => setPanelOpen(false)} className={secondaryButton}>
              <User size={16} aria-hidden="true" /><span className="max-w-28 truncate" title={displayName}>{displayName}님</span>
            </Link>
          ) : <Link href="/login" className={primaryButton}><LogIn size={16} aria-hidden="true" />로그인</Link>}
        </div>

        <div className="flex shrink-0 items-center gap-2 xl:hidden">
          {user ? <Link href="/my" title="마이페이지" aria-label="마이페이지" className="flex h-10 w-10 items-center justify-center rounded-md text-[#45404d] hover:bg-[#f5f4f7]"><User size={19} aria-hidden="true" /></Link>
            : <Link href="/login" className={`${primaryButton} px-3`}><LogIn size={16} aria-hidden="true" />로그인</Link>}
          <button ref={mobileButtonRef} type="button" className="flex h-10 w-10 items-center justify-center rounded-md border border-[#e3e0e8] text-[#45404d] hover:bg-[#f8f7fa]"
            onClick={() => { setMobileOpen(current => !current); setPanelOpen(false); }}
            aria-label={mobileOpen ? "메뉴 닫기" : "메뉴 열기"} aria-expanded={mobileOpen} aria-controls="mobile-navigation">
            {mobileOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav id="mobile-navigation" aria-label="모바일 메뉴" className="max-h-[calc(100dvh-73px)] overflow-y-auto border-t border-[#e9e7ed] bg-white px-5 py-5 xl:hidden">
          <div className="mx-auto max-w-6xl">
            <div className="grid grid-cols-2 gap-1 text-sm font-semibold text-[#45404d]">
              {navigation.map(item => <Link key={item.href} href={item.href} onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-3 hover:bg-[#f5f4f7]">{item.title}</Link>)}
              <a href="https://status.lighthost.it.kr" target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="inline-flex items-center gap-2 rounded-md px-3 py-3 hover:bg-[#f5f4f7]"><Activity size={16} aria-hidden="true" />서버 상태</a>
              <Link href="/donate" onClick={() => setMobileOpen(false)} className="inline-flex items-center gap-2 rounded-md px-3 py-3 text-[#8f4fe8] hover:bg-[#f5f4f7]"><Heart size={16} aria-hidden="true" />후원</Link>
            </div>
            <p className="mb-2 mt-5 px-3 text-xs font-semibold text-[#96919d]">관리 콘솔</p>
            <div className="grid gap-1">
              {panelLinks.filter(item => item.ready).map(item => {
                const Icon = item.icon;
                return <Link key={item.title} href={item.href} target={item.external ? "_blank" : undefined} rel={item.external ? "noreferrer" : undefined}
                  onClick={() => setMobileOpen(false)} className="inline-flex items-center gap-3 rounded-md px-3 py-3 text-sm font-semibold text-[#45404d] hover:bg-[#f5f4f7]">
                  <Icon size={17} aria-hidden="true" /><span className="flex-1">{item.title}</span>{item.external && <ExternalLink size={14} aria-hidden="true" className="text-[#96919d]" />}
                </Link>;
              })}
            </div>
            {user && <div className="mt-4 flex flex-wrap items-center gap-2 px-3 text-sm">
              <Link href="/my" onClick={() => setMobileOpen(false)} className="inline-flex min-h-10 items-center gap-2 font-semibold text-[#45404d]"><User size={16} aria-hidden="true" />마이페이지</Link>
              {isAdmin && <Link href="/admin" onClick={() => setMobileOpen(false)} className="inline-flex min-h-10 items-center gap-2 px-2 text-[#8f4fe8]"><Shield size={16} aria-hidden="true" />관리자 대시보드</Link>}
              <form action="/api/logout" method="post" className="ml-auto"><button type="submit" className="inline-flex min-h-10 items-center gap-2 text-[#77717e]"><LogOut size={16} aria-hidden="true" />로그아웃</button></form>
            </div>}
          </div>
        </nav>
      )}
    </header>
  );
}
